import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CloudOff, RotateCcw, ServerCrash, WifiOff } from 'lucide-react';
import { http, setConnectionHandler } from '@/api/client';
import type { ConnectionState } from '@/api/types';

type ConnectionValue = {
  state: ConnectionState;
  /** Increments on every successful recovery; use as a key to refetch. */
  recoveries: number;
  retry: () => Promise<boolean>;
  isRetrying: boolean;
};

const ConnectionContext = createContext<ConnectionValue>({
  state: 'online',
  recoveries: 0,
  retry: async () => true,
  isRetrying: false,
});

export const useConnection = () => useContext(ConnectionContext);

/**
 * Watches the link between this browser and the API.
 *
 * Two distinct failures get two distinct messages, because they are not the
 * user's problem in the same way:
 *
 *   offline    — the device has no network. Told to check their connection.
 *   serverDown — the device is fine, our API is not answering. Told it is us.
 *
 * `navigator.onLine` alone is not enough: it reports `true` on a Wi-Fi network
 * with no route to the internet, which is exactly the case that confuses
 * people. So the real signal is a request that came back with no response at
 * all, and `navigator.onLine` is used only to choose which sentence to show.
 */
export function ConnectionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConnectionState>('online');
  const [recoveries, setRecoveries] = useState(0);
  const [isRetrying, setRetrying] = useState(false);
  const stateRef = useRef<ConnectionState>('online');

  const apply = useCallback((next: ConnectionState) => {
    if (stateRef.current === next) return;
    // Coming back up is an event other components want to react to.
    if (next === 'online' && stateRef.current !== 'online') {
      setRecoveries((count) => count + 1);
    }
    stateRef.current = next;
    setState(next);
  }, []);

  // Every request reports its outcome through this handler.
  useEffect(() => {
    setConnectionHandler(apply);
    return () => setConnectionHandler(null);
  }, [apply]);

  // The browser's own events are a hint, not the truth: `online` only means a
  // network interface came up, so we verify with a real request before
  // clearing the banner.
  useEffect(() => {
    const goOffline = () => apply('offline');
    const goOnline = () => void probe();

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  });

  const probe = useCallback(async (): Promise<boolean> => {
    try {
      // /health is the cheapest endpoint that proves the whole path works:
      // browser → nginx → uvicorn → PostgreSQL.
      await http.get('/health', { timeout: 8000, baseURL: '/' });
      apply('online');
      return true;
    } catch {
      apply(
        typeof navigator !== 'undefined' && navigator.onLine === false
          ? 'offline'
          : 'serverDown',
      );
      return false;
    }
  }, [apply]);

  // While down, poll gently. Backing off matters: a phone in a lift should not
  // fire a request every second and drain the battery.
  useEffect(() => {
    if (state === 'online') return;
    let attempt = 0;
    let timer: number;

    const schedule = () => {
      const delay = Math.min(30_000, 3_000 * 2 ** attempt);
      timer = window.setTimeout(async () => {
        attempt += 1;
        const ok = await probe();
        if (!ok) schedule();
      }, delay);
    };

    schedule();
    return () => clearTimeout(timer);
  }, [state, probe]);

  const retry = useCallback(async () => {
    setRetrying(true);
    try {
      return await probe();
    } finally {
      setRetrying(false);
    }
  }, [probe]);

  const value = useMemo(
    () => ({ state, recoveries, retry, isRetrying }),
    [state, recoveries, retry, isRetrying],
  );

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
}

/**
 * The banner. Deliberately not a full-screen block: the page the user was
 * reading stays visible and readable, because cached content is still useful
 * information. Only writes will fail, and those surface their own errors.
 */
export function ConnectionBanner() {
  const { t } = useTranslation();
  const { state, retry, isRetrying } = useConnection();

  if (state === 'online') return null;

  const offline = state === 'offline';

  return (
    <div className="connection-banner" role="alert" aria-live="assertive">
      <span className="connection-banner__icon">
        {offline ? <WifiOff size={18} /> : <ServerCrash size={18} />}
      </span>
      <div>
        <b>{offline ? t('connection.offlineTitle') : t('connection.serverTitle')}</b>
        <small>{offline ? t('connection.offlineBody') : t('connection.serverBody')}</small>
      </div>
      <button type="button" className="button small" disabled={isRetrying} onClick={() => void retry()}>
        <RotateCcw size={14} aria-hidden />
        {isRetrying ? t('connection.retrying') : t('errors.retry')}
      </button>
    </div>
  );
}

/**
 * Full-screen version, for when there is nothing cached worth showing — a
 * first load that never reached the API.
 */
export function ConnectionScreen() {
  const { t } = useTranslation();
  const { state, retry, isRetrying } = useConnection();
  const offline = state === 'offline';

  return (
    <div className="connection-screen" role="alert">
      <CloudOff size={40} aria-hidden />
      <h2>{t('errors.crashTitle')}</h2>
      <p>{t('errors.crashBody')}</p>
      <p className="muted">
        {offline ? t('connection.offlineBody') : t('connection.serverBody')}
      </p>
      <button type="button" className="button" disabled={isRetrying} onClick={() => void retry()}>
        <RotateCcw size={16} aria-hidden />
        {isRetrying ? t('connection.retrying') : t('errors.retry')}
      </button>
      <a className="text-link" href="/support#contact">
        {t('footer.contact')}
      </a>
    </div>
  );
}
